import React from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  alpha,
  Divider,
  useMediaQuery,
  Modal,
  Paper,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';
import { ActivityTypes, logActivity } from '../utils/activityLogger';
import useIdleTimeout from '../hooks/useIdleTimeout';

// Add notification sound utility
const playNotificationSound = () => {
  try {
    const audio = new Audio('/sounds/notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(error => {
      // Only log if it's not a user interaction error
      if (error.name !== 'NotAllowedError') {
        console.error('Error playing notification sound:', error);
      }
    });
  } catch (error) {
    console.error('Error initializing notification sound:', error);
  }
};

const Header = ({ onLogout, onMenuClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = React.useState(null);
  const [selectedNotification, setSelectedNotification] = React.useState(null);
  const [soundEnabled, setSoundEnabled] = React.useState(() => {
    const saved = localStorage.getItem('notificationSoundEnabled');
    return saved ? JSON.parse(saved) : true;
  });
  const queryClient = useQueryClient();

  // Add idle timeout hook
  const { SessionTimeoutModal } = useIdleTimeout(20); // set minutes timeout

  // Get user data from localStorage
  const getUserName = () => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        return userData.fullname || 'Administrator';
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return 'Administrator';
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleNotificationsMenuOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    // Mark all notifications as viewed when opening the menu
    if (unreadCount?.count > 0) {
      // Mark each notification as viewed individually
      notificationsData?.data?.forEach(notification => {
        if (notification.status === 'pending') {
          api.notifications.markAsViewed(notification._id)
            .then(() => {
              // Log activity for each notification marked as viewed
              logActivity({
                action: ActivityTypes.NOTIFICATION_MARK_VIEWED,
                details: `Marked notification as viewed from header: ${notification.title}`,
                metadata: {
                  notificationId: notification._id,
                  notificationTitle: notification.title
                }
              });
            })
            .catch(error => {
              console.error('Error marking notification as viewed:', error);
            });
        }
      });
      // Refetch notifications after marking as viewed
      queryClient.invalidateQueries(['adminNotifications']);
      queryClient.invalidateQueries(['unreadNotifications']);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationsAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  // Save sound preference to localStorage
  const toggleSound = () => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('notificationSoundEnabled', JSON.stringify(newValue));
      return newValue;
    });
  };

  // Fetch notifications with error handling and shorter interval
  const { data: notificationsData, error: notificationsError } = useQuery({
    queryKey: ['adminNotifications'],
    queryFn: () => api.notifications.getNotifications(1, 4),
    refetchInterval: 10000, // Check every 10 seconds
    retry: 1,
  });

  // Fetch unread count with error handling and shorter interval
  const { data: unreadCount, error: unreadError } = useQuery({
    queryKey: ['unreadNotifications'],
    queryFn: () => api.notifications.getUnreadCount(),
    refetchInterval: 10000, // Check every 10 seconds
    retry: 1,
  });

  // Play sound when there are unread notifications
  React.useEffect(() => {
    if (unreadCount?.count > 0 && soundEnabled) {
      playNotificationSound();
    }
  }, [unreadCount?.count, soundEnabled]);

  const handleViewAllNotifications = () => {
    handleMenuClose();
    navigate('/dashboard/allnotifications');
    // Log activity
    logActivity({
      action: ActivityTypes.NOTIFICATION_VIEW,
      details: 'Navigated to all notifications page from header',
      metadata: {
        source: 'header',
        action: 'view_all'
      }
    });
  };

  const handleNotificationClick = async (notification) => {
    try {
      await api.notifications.markAsViewed(notification._id);
      setSelectedNotification(notification);
      handleMenuClose();
      // Log activity
      await logActivity({
        action: ActivityTypes.NOTIFICATION_VIEW,
        details: `Viewed notification from header: ${notification.title}`,
        metadata: {
          notificationId: notification._id,
          notificationTitle: notification.title,
          notificationType: notification.type
        }
      });
      // Refetch notifications after marking as viewed
      queryClient.invalidateQueries(['adminNotifications']);
      queryClient.invalidateQueries(['unreadNotifications']);
    } catch (error) {
      console.error('Error marking notification as viewed:', error);
    }
  };

  const handleCloseModal = () => {
    setSelectedNotification(null);
  };

  // Add chat link to navigation menu
  const menuItems = [
    {
      text: 'Chat Support',
      icon: <ChatIcon />,
      path: '/chat',
      color: 'primary'
    },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - 280px)` },
          ml: { sm: `280px` },
          bgcolor: theme.palette.background.paper,
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={onMenuClick}
                sx={{
                  mr: 2,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: '0.5px',
              }}
            >
              Welcome back, Admin
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={handleNotificationsMenuOpen}
              sx={{
                position: 'relative',
                color: theme.palette.text.secondary,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
              }}
            >
              <Badge
                badgeContent={unreadError ? '!' : (unreadCount?.count || 0)}
                color={unreadError ? 'error' : 'error'}
                sx={{
                  '& .MuiBadge-badge': {
                    animation: unreadCount?.count > 0 ? 'pulse 2s infinite' : 'none',
                  },
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                    '50%': {
                      transform: 'scale(1.2)',
                      opacity: 0.8,
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton
              size="large"
              onClick={handleProfileMenuOpen}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  color: theme.palette.primary.main,
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                A
              </Avatar>
            </IconButton>
          </Box>

          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                boxShadow: theme.shadows[3],
                borderRadius: 2,
                bgcolor: theme.palette.background.paper,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                {getUserName()}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {getUserName().split(' ')[1]}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { handleMenuClose(); navigate('/dashboard/users'); }}>
              <PersonIcon sx={{ mr: 2, fontSize: 20, color: theme.palette.text.secondary }} />
              Users Management
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/dashboard/settings'); }}>
              <SettingsIcon sx={{ mr: 2, fontSize: 20, color: theme.palette.text.secondary }} />
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: theme.palette.error.main }}>
              <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationsAnchorEl}
            open={Boolean(notificationsAnchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 320,
                maxWidth: 360,
                boxShadow: theme.shadows[3],
                borderRadius: 2,
                bgcolor: theme.palette.background.paper,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Notifications
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    handleMenuClose();
                    toggleSound();
                  }}
                  sx={{
                    color: soundEnabled ? theme.palette.primary.main : theme.palette.text.secondary,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {soundEnabled ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {notificationsData?.data?.length > 0 ? (
                notificationsData.data.map((notification) => (
                  <MenuItem
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                      bgcolor: notification.status === 'pending' ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                        {notification.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {notification.content}
                      </Typography>
                      {notification.transaction_details?.updatedBy && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                          Updated by: {notification.transaction_details.updatedBy.name} ({notification.transaction_details.updatedBy.username})
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: theme.palette.text.disabled, mt: 0.5 }}>
                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    No notifications
                  </Typography>
                </MenuItem>
              )}
            </Box>
            <Divider />
            <MenuItem
              onClick={handleViewAllNotifications}
              sx={{
                justifyContent: 'center',
                color: theme.palette.primary.main,
                py: 1.5,
              }}
            >
              View All Notifications
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Notification Details Modal */}
      <Modal
        open={Boolean(selectedNotification)}
        onClose={handleCloseModal}
        aria-labelledby="notification-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'hidden',
            mx: 2,
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: theme.palette.primary.main }} />
              <Typography
                id="notification-modal-title"
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                Notification Details
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3, overflow: 'auto' }}>
            {selectedNotification && (
              <>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 2,
                    fontSize: '1.5rem',
                  }}
                >
                  {selectedNotification.title}
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.6),
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                    }}
                  >
                    {selectedNotification.content}
                  </Typography>
                </Box>

                {/* Transaction Details */}
                {selectedNotification.transaction_details && (
                  <Box
                    sx={{
                      p: 2,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.palette.primary.main,
                        mb: 1,
                        fontWeight: 600,
                      }}
                    >
                      Transaction Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {Object.entries(selectedNotification.transaction_details).map(([key, value]) => (
                        <Box
                          key={key}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              textTransform: 'capitalize',
                            }}
                          >
                            {key.replace(/_/g, ' ')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                              fontWeight: 500,
                            }}
                          >
                            {typeof value === 'object' && value !== null
                              ? `${value.name} (${value.username})`
                              : typeof value === 'number'
                                ? `₦${value.toLocaleString()}`
                                : value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Footer */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: theme.palette.text.disabled,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    {formatDistanceToNow(new Date(selectedNotification.createdAt), { addSuffix: true })}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </Modal>

      {/* Session Timeout Modal */}
      {SessionTimeoutModal}
    </>
  );
};

export default Header; 